/**
 * SDK Error Classes
 */

/**
 * `SDKResponse.code` of a priced read that asked for a currency the shop does
 * not sell in (ADR-0066). `errorParams.requested` names the currency,
 * `errorParams.available` lists the enabled ones.
 */
export const CURRENCY_UNAVAILABLE_ERROR_CODE = 'errors.storefront.currency_unavailable';

export class ShoppexError extends Error {
  public readonly code: string;
  public readonly statusCode?: number;

  constructor(message: string, code: string, statusCode?: number) {
    super(message);
    this.name = 'ShoppexError';
    this.code = code;
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, ShoppexError.prototype);
  }
}

export class NotInitializedError extends ShoppexError {
  constructor() {
    super(
      'SDK not initialized. Call shoppex.init() first.',
      'NOT_INITIALIZED'
    );
    this.name = 'NotInitializedError';
    Object.setPrototypeOf(this, NotInitializedError.prototype);
  }
}

export class NetworkError extends ShoppexError {
  constructor(message: string, statusCode?: number) {
    super(message, 'NETWORK_ERROR', statusCode);
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * A refusal the SERVER made and named. `code` is the backend's `error_code`
 * (e.g. `errors.checkout.coupon_no_longer_valid`) — the branchable identity of
 * the refusal, stable across locales, unlike the localized `message`.
 *
 * Distinct from {@link NetworkError}, whose `code` is always `NETWORK_ERROR`:
 * that one means "the request did not produce a named answer".
 */
export class ApiError extends ShoppexError {
  public readonly errorParams?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    errorParams?: Record<string, unknown>,
  ) {
    super(message, code, statusCode);
    this.name = 'ApiError';
    this.errorParams = errorParams;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export class ValidationError extends ShoppexError {
  public readonly invalidFields?: string[];

  constructor(message: string, invalidFields?: string[]) {
    super(message, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
    this.invalidFields = invalidFields;
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class CartError extends ShoppexError {
  constructor(message: string) {
    super(message, 'BASKET_ERROR');
    this.name = 'CartError';
    Object.setPrototypeOf(this, CartError.prototype);
  }
}
